import dayjs, { type Dayjs } from "dayjs"

import { type ClosingTimes, type MappedHolidays } from "@/types"
import {
  type Booking,
  BookingDaysEnum,
  BookingTimeCategory,
  BookingTypeEnum,
  type BookingTypeObject,
  type Child,
  type ManuallySet,
  type ShortTime,
  type Vacation1,
  type Vacation2,
  type Vacation3,
} from "@/graphql/types"
import {
  type BookingFormModel,
  findStandardBooking,
  type VacationBooking,
  validateDate,
} from "@/utils/bookings"
import { calculateAverageTimeCategory } from "@/utils/calculateFee"
import {
  calculateAge,
  getMonthsBetweenDatesWithYear,
  Month,
  countDatesBetween,
  getKindergartenYear,
  reachesAgeInMonth,
} from "@/utils/calendar"
import { ensureDayjs, hasOverlapOrInside } from "@/utils/date"
import { BookingTime, BookingTimeFactors, numberToBookingTime, SubsidyState } from "@/utils/subsidy"

import { formatPattern, kibigChildIdMusterTokens, kibigChildNameTokens } from "../formatPattern"
import { areFieldValuesUnique } from "../misc"

/** Belegung */
export enum Allocation {
  KeineBelegung = 0,
  NichtFoerderfaehig = 1,
  Foerderfaehig = 2,
}

/**
 * § 24 (5) sentence 2 1. and sentence 5f BayKiBiG
 */
export function countsAsU3(booking: Booking | BookingFormModel, child: Child, refdate: Dayjs) {
  const birthdate = dayjs(child.birth.date)
  const eom = refdate.endOf("month")
  const age = calculateAge(birthdate, eom)
  if (age < 3) return true // sentence 2 point 1
  if (booking.nurseryChild) {
    const kgj = getKindergartenYear(refdate.toDate())
    const endOfSeptember = dayjs(kgj.start).endOf("month")
    // NOTE: To behave identically to the desktop program, we don't use `booking.from` here, like
    //       in FormReservation.vue, and instead use `endOfSeptember`. This is an inconsistency
    //       we copy for now, until we've reached full clarity on the matter.
    if (booking.nurseryChildException && reachesAgeInMonth(3, birthdate, endOfSeptember)) {
      return true
    }
    // Otherwise, calculate the age at the start of the kgj according to the month principle.
    const age = calculateAge(birthdate, endOfSeptember)
    return age < 3
  }
  return false
}

/**
 * Implements https://digitale-verwaltung.atlassian.net/wiki/spaces/adebisKITA/pages/1661829137/
 * See also `useBookingStatus` and `FormReservation.vue`.
 */
export function getSubsidyState(
  booking: Booking | BookingFormModel,
  child: Child,
  u3: boolean,
  withAssistedIntegration = true
): SubsidyState {
  if (!child.birth.date) {
    console.error(`Missing birth date for child:`, child)
    return SubsidyState.nil
  }
  const schoolKid = booking.isSchoolChild
  if (withAssistedIntegration && booking.assistedIntegration) {
    if (booking.migration) {
      return SubsidyState.X09
    }
    if (u3) {
      return SubsidyState.X08
    }
    if (schoolKid) {
      return SubsidyState.X07
    }
    return SubsidyState.X06
  }

  if (booking.migration) {
    if (u3) {
      return SubsidyState.X05
    }
    if (schoolKid) {
      return SubsidyState.X04
    }
    return SubsidyState.X03
  }

  if (u3) {
    return SubsidyState.X02
  }
  if (schoolKid) {
    return SubsidyState.X01
  }
  return SubsidyState.X00
}

export function determineBookingTime(
  booking: Booking | BookingFormModel,
  alternativeBookings: Booking[] = []
) {
  let standardCategory = booking.timeFactor.category
  const hasDifferentCategory = alternativeBookings.some(
    (b) => b.timeFactor?.category !== standardCategory
  )

  if (hasDifferentCategory) {
    standardCategory = calculateAverageTimeCategory(
      booking?.timeFactor?.hoursPerWeek ?? 0,
      alternativeBookings
    )
  }
  return categoryToBookingTime(standardCategory)
}

/**
 * Implements § 24 (4) sentence 4 BayKiBiG and § 25 (1) sentence 3 AVBayKiBiG.
 */
export function isBookingTimeSubsidizable(
  bookingTime: BookingTime,
  booking: Booking | BookingFormModel,
  u3: boolean
) {
  if (!u3 && !booking.isSchoolChild && [BookingTime.Y00, BookingTime.Y01].includes(bookingTime)) {
    // has completed their third year of life, is not a school child, and the booking time is < 3 hours
    // -> this booking (time) is not subsidizable
    return false
  }
  return true
}

export function categoryToBookingTime(category: BookingTimeCategory) {
  switch (category) {
    case BookingTimeCategory.Zero:
      return BookingTime.nil
    case BookingTimeCategory.One:
      return BookingTime.Y00
    case BookingTimeCategory.Two:
      return BookingTime.Y01
    case BookingTimeCategory.Three:
      return BookingTime.Y02
    case BookingTimeCategory.Four:
      return BookingTime.Y03
    case BookingTimeCategory.Five:
      return BookingTime.Y04
    case BookingTimeCategory.Six:
      return BookingTime.Y05
    case BookingTimeCategory.Seven:
      return BookingTime.Y06
    case BookingTimeCategory.Eight:
      return BookingTime.Y07
    case BookingTimeCategory.Nine:
    case BookingTimeCategory.Ten:
    case BookingTimeCategory.Eleven:
    case BookingTimeCategory.Twelve:
      return BookingTime.Y08
    default: {
      const exhaustiveCheck: never = category
      throw `Unrecognized booking time category ${exhaustiveCheck as BookingTimeCategory}!`
    }
  }
}

export enum PreCourse {
  No = 0,
  FirstKjg = 1,
  SecondKgj = 2,
  BothKgj = 3,
}
const preCourse = (kgj1: boolean, kgj2: boolean) =>
  kgj1 && kgj2
    ? PreCourse.BothKgj
    : kgj1
      ? PreCourse.FirstKjg
      : kgj2
        ? PreCourse.SecondKgj
        : PreCourse.No

export type KibigMonthlyBooking = {
  /** Belegung */
  allocation: Allocation
  /** U3 status */
  u3: boolean
  /** Förderstatus */
  subsidyState: SubsidyState
  /** Förderstatus ohne Eingliederungshilfe (not sent to KiBig, just for analysis) */
  subsidyStateWithoutAssistedIntegration: SubsidyState
  /** Buchungszeit */
  bookingTime: BookingTime
  /** AGS der Förderkommune */
  communeKey: string
  /** Elternbeitragsfreiheit */
  parentalFeeExemption: "0" | "1" | ""
  /** Beitragsersatz */
  parentalFeeSubsidy: "0" | "1" | ""
  /** Eingliederungshilfe (not sent to KiBig, just for analysis) */
  assistedIntegration: boolean
  /** Bezirksförderung (not sent to KiBig, just for analysis) */
  districtSubsidy: boolean
  /** Schulkind (not sent to KiBig, just for analysis of parental fee subsidy eligibility) */
  isSchoolChild: boolean
}

type BookingMonths = Map<Month, KibigMonthlyBooking>

export type KibigChild = {
  $child: Child
  $name: string // full name
  /** Used for KiBiG "Freie ID". Configuration-dependant! */
  id: string
  /** Used for KiBiG "Bezeichnung". Should be anonymized! */
  name: string
  bookings: BookingMonths
  preCourse: PreCourse
  vacationDays: number
  vacationSubsidyState: SubsidyState
  vacationBookingTime: BookingTime
  vacationCommuneKey: string
  shortTimeDays: number
  shortTimeSubsidyState: SubsidyState
  shortTimeBookingTime: BookingTime
  shortTimeCommuneKey: string
  /** Only used for parental fee subsidy eligibility determination in analysis.ts */
  birthdate: Dayjs
}

export type KibigChildConfig = {
  altId: boolean
  childIdMuster: string
  childNameMuster: string
}

export type KibigEmployeeConfig = {
  employeeIdMuster: string
  altPersId: boolean
}

export type KibigConfig = {
  child: KibigChildConfig
  employee: KibigEmployeeConfig
}

export function calculateChildrenKibigMonths(
  allChildren: Child[],
  year: number,
  untilMonth: number,
  holidays: MappedHolidays,
  closingTimes: ClosingTimes,
  kibigConfig?: KibigChildConfig
): KibigChild[] {
  const res = []
  const children = filterChildrenAndBookings(allChildren, year, untilMonth).sort(
    (c1, c2) => c1.n - c2.n
  )
  for (const child of children) {
    res.push(mapChildToKibig(child, year, holidays, closingTimes, kibigConfig))
  }
  return res
}

/** Child with filtered & sorted bookings */
type ChildWithBookings = Child & { bookings: Booking[] }
function filterChildrenAndBookings(
  children: Child[],
  year: number,
  untilMonth: number
): ChildWithBookings[] {
  const yearStart = dayjs(`${year}`)
  const yearEnd = dayjs(`${year}-${untilMonth}`).endOf("month")
  const childrenWithBookings = (children as (Child & { bookings: Booking[] })[]).filter((child) => {
    const filteredContracts = child.contracts.filter(
      (c) =>
        (c.state.__typename === "Contracted" || c.state.__typename === "Terminated") &&
        hasOverlapOrInside(c.startOfContract, c.endOfContract, yearStart, yearEnd)
    )
    if (!filteredContracts.length) {
      console.debug(`Filtering child because no active contract in year ${year}`, child)
      return false
    }
    child.contracts = filteredContracts
    const filteredBookings = Array.from(filterChildBookings(child, yearStart, yearEnd)).sort(
      (b1, b2) => dayjs(b1.from).diff(b2.from)
    )
    if (!filteredBookings.length) {
      console.debug(`Filtering child because no active booking(s) in year ${year}`, child)
      return false
    }
    child.bookings = filteredBookings
    return true
  })
  return childrenWithBookings
}

function* filterChildBookings(child: Child, yearStart: Dayjs, yearEnd: Dayjs) {
  for (const contract of child.contracts) {
    for (const booking of contract.bookings) {
      if (
        hasOverlapOrInside(booking.from, booking.to ?? contract.endOfContract, yearStart, yearEnd)
      ) {
        yield { ...booking, contract }
      }
    }
  }
}

type CommuneKey = string
type CommuneDays = Record<CommuneKey, number>

/**
 * This implements most of the child subsidy calculation (Förderberechnung).
 * Summing up and multiplying all the monetary amounts happens in analysis.ts.
 */
function mapChildToKibig(
  child: ChildWithBookings,
  year: number,
  holidays: MappedHolidays,
  closingTimes: ClosingTimes,
  kibigConfig?: KibigChildConfig
): KibigChild {
  let preCourseKgj1 = false
  let preCourseKgj2 = false
  const communeDays: CommuneDays = {}
  const vacationData = {
    divident: 0,
    days: 0, // also divisor
  }
  const shortTimeData = {
    days: 0,
    bookings: <
      {
        commune: string
        subsidyState: SubsidyState
        bookingTime: BookingTime
      }[]
    >[],
  }

  const alternativeBookings = child.bookings.filter(
    ({ type }) => type === BookingTypeEnum.Alternative_1 || type === BookingTypeEnum.Alternative_2
  )
  const bookingMonths: BookingMonths = new Map()
  // insert default values (in month-sorted order for XML)
  for (let i = 0; i < 12; i++) {
    bookingMonths.set(i as Month, {
      allocation: Allocation.KeineBelegung,
      u3: false,
      subsidyState: SubsidyState.nil,
      subsidyStateWithoutAssistedIntegration: SubsidyState.nil,
      bookingTime: BookingTime.nil,
      communeKey: "",
      parentalFeeExemption: "0",
      parentalFeeSubsidy: "0",
      assistedIntegration: false,
      districtSubsidy: false,
      isSchoolChild: false,
    })
  }

  const startOfYear = dayjs(`${year}-01-01T12:00:00Z`)
  const clampFromDate = (booking: Booking) => dayjs.max(startOfYear, dayjs(booking.from))

  // todo: consider using calculateFee.ts::getToDate
  const endOfYear = dayjs(`${year}`).endOf("year")
  const getToRefDate = (booking: Booking) => {
    const dates = [booking.to, booking.contract.endOfContract, endOfYear]
      .filter((date) => !!date)
      .map((date) => ensureDayjs(date))
    return dayjs.min(dates) as Dayjs
  }

  const getBookingDaysByCalendar = (booking: Booking) =>
    booking.bookingDays.filter(({ date }) => date.startsWith(`${year}`)).length
  // Note: This is *very* slow due to calling into Dayjs many, many times. The Temporal API will be much faster.
  const getBookingDaysByOpeningDays = (booking: Booking) =>
    countDatesBetween(
      clampFromDate(booking).toISOString(),
      getToRefDate(booking).toISOString(),
      (date) => validateDate(holidays, closingTimes, date)
    )

  // this iterates in order of booking.from
  for (const booking of child.bookings) {
    switch (booking.type) {
      case BookingTypeEnum.Standard: {
        const bookingFrom = clampFromDate(booking)
        const bookingTo = getToRefDate(booking)
        const monthYears = getMonthsBetweenDatesWithYear(bookingFrom, bookingTo, "M")
        for (const { month: m, year: y } of monthYears) {
          if (y !== `${year}`) continue // should never happen

          const endOfMonth = dayjs(`${y}-${m}-10`).endOf("month")
          if (!endOfMonth.isBetween(bookingFrom, bookingTo, "day", "[]")) {
            continue // Monatsprinzip TODO: § 25(2) AVBayKiBiG ?
          }

          const m1 = Number.parseInt(m, 10)
          if (booking.preCourse) {
            if (m1 < 9) {
              preCourseKgj1 = true
            } else {
              preCourseKgj2 = true
            }
          }
          bookingMonths.set(
            (m1 - 1) as Month,
            mapStandardBooking(
              booking,
              alternativeBookings.filter((b) =>
                endOfMonth.isBetween(dayjs(b.from), dayjs(b.to), "date", "[]")
              ),
              child,
              endOfMonth
            )
          )
        }

        // determine the commune with the most standard booking days
        // (will be used as the subsidy commune for vacation & short time bookings)
        const communeKey = booking.commune?.communeKey
        if (communeKey) {
          const days = getBookingDaysByOpeningDays(booking)
          communeDays[communeKey] = (communeDays[communeKey] ?? 0) + days
        }
        break
      }
      case BookingTypeEnum.Alternative_1:
      case BookingTypeEnum.Alternative_2:
        break // handled in Standard case / mapStandardBooking
      case BookingTypeEnum.ShortTime: // todo: ask Albert how to handle `u3` here, since it affects booking time
      case BookingTypeEnum.Vacation_1:
      case BookingTypeEnum.Vacation_2:
      case BookingTypeEnum.Vacation_3: {
        const bookingTime = categoryToBookingTime(booking.timeFactor.category)
        if (!bookingTime) {
          console.warn(`Zero booking time for ${booking.type} booking`, booking)
        }
        const factor = BookingTimeFactors[bookingTime]
        let obj: BookingTypeObject
        switch (booking.type) {
          case BookingTypeEnum.ShortTime: {
            obj = booking.typeObject as ShortTime
            let days = 0
            switch (obj.bookingDaysEnum) {
              case BookingDaysEnum.AccordingToCalendar:
                days = getBookingDaysByCalendar(booking)
                break
              case BookingDaysEnum.AccordingToOpeningDays:
                days = getBookingDaysByOpeningDays(booking)
                break
              case BookingDaysEnum.ManuallySet: {
                days = (obj.bookingDays as ManuallySet).days
              }
            }
            shortTimeData.days += days
            if (!booking.commune!.communeKey) {
              console.warn("Short time booking missing required commune: ", booking)
            }
            shortTimeData.bookings.push({
              commune: booking.commune!.communeKey,
              subsidyState: getSubsidyState(
                booking,
                child,
                countsAsU3(booking, child, getToRefDate(booking))
              ),
              bookingTime: bookingTime,
            })
            break
          }
          case BookingTypeEnum.Vacation_1:
          case BookingTypeEnum.Vacation_2:
          case BookingTypeEnum.Vacation_3: {
            obj = booking.typeObject as Vacation1 | Vacation2 | Vacation3
            const standardBooking = findStandardBooking(booking as VacationBooking, child.bookings)
            if (!standardBooking) {
              console.error(`Could not find standard booking for vacation booking`, booking)
              continue
            }
            let days = 0
            switch (obj.bookingDaysEnum) {
              case BookingDaysEnum.AccordingToCalendar:
                days = getBookingDaysByCalendar(booking)
                break
              case BookingDaysEnum.AccordingToOpeningDays:
                days = getBookingDaysByOpeningDays(booking)
                break
              case BookingDaysEnum.ManuallySet: {
                days = (obj.bookingDays as ManuallySet).days
              }
            }
            vacationData.days += days
            vacationData.divident += days * factor
          }
        }
        break
      }
      default: {
        const exhaustiveCheck: never = booking.type
        console.error("Unknown booking type", exhaustiveCheck, "!")
      }
    }
  }

  const lastBooking = child.bookings[child.bookings.length - 1]
  const lastStandardSubsidyState = getSubsidyState(
    lastBooking,
    child,
    countsAsU3(lastBooking, child, getToRefDate(lastBooking))
  )
  const communeWithMostStandardDays = findByMaxDays(communeDays)
  const canUseVacation =
    vacationData.days > 0 &&
    !!communeWithMostStandardDays &&
    lastStandardSubsidyState !== SubsidyState.nil
  // For short time bookings, we take the values from the first such booking
  const firstShortTimeBooking = shortTimeData.bookings.values().next().value
  const canUseShortTime = firstShortTimeBooking !== undefined && shortTimeData.days > 0

  const childInfoForPatterns = {
    id: child.id,
    firstName: child.person.firstname,
    lastName: child.person.lastname,
    childId: child.childId,
    altChildId: child.altChildId,
  }
  const id = getKibigChildId(childInfoForPatterns, kibigConfig)
  const name = getKibigChildName(childInfoForPatterns, kibigConfig)

  const record: Omit<KibigChild, "$child"> = {
    $name: `${child.person.lastname} ${child.person.firstname}`,
    id,
    // todo: support anonymized export checkboxes
    name,
    bookings: bookingMonths,
    preCourse: preCourse(preCourseKgj1, preCourseKgj2),
    vacationDays: canUseVacation ? (Math.min(vacationData.days, 75) ?? 0) : 0, // API limit
    vacationSubsidyState: canUseVacation ? lastStandardSubsidyState : SubsidyState.nil,
    vacationBookingTime: canUseVacation
      ? numberToBookingTime(vacationData.divident / vacationData.days)
      : BookingTime.nil,
    vacationCommuneKey: canUseVacation ? (communeWithMostStandardDays ?? "") : "",
    shortTimeDays: canUseShortTime ? (Math.min(shortTimeData.days, 99) ?? 0) : 0, // API limit
    shortTimeSubsidyState: canUseShortTime ? firstShortTimeBooking.subsidyState : SubsidyState.nil,
    shortTimeBookingTime: canUseShortTime ? firstShortTimeBooking.bookingTime : BookingTime.nil,
    shortTimeCommuneKey: canUseShortTime ? firstShortTimeBooking.commune : "",
    birthdate: dayjs(child.birth.date),
  }
  return Object.defineProperty(record, "$child", {
    value: child,
  }) as KibigChild
}

function findByMaxDays(communes: CommuneDays): CommuneKey | undefined {
  let winner = null
  for (const [key, days] of Object.entries(communes)) {
    if (!winner || days >= winner.days) {
      winner = { communeKey: key, days }
    }
  }
  return winner?.communeKey
}

function mapStandardBooking(
  booking: Booking,
  alternativeBookings: Booking[],
  child: Child,
  refdate: Dayjs
): KibigMonthlyBooking {
  const u3 = countsAsU3(booking, child, refdate)
  const subsidyState = getSubsidyState(booking, child, u3)
  const bookingTime = determineBookingTime(booking, alternativeBookings)
  let allocation = Allocation.Foerderfaehig
  if (bookingTime === BookingTime.nil) {
    allocation = Allocation.KeineBelegung
  } else if (booking.exceptionForSubsidy || !isBookingTimeSubsidizable(bookingTime, booking, u3)) {
    allocation = Allocation.NichtFoerderfaehig
  }

  if (allocation === Allocation.Foerderfaehig && !booking.commune) {
    console.warn(
      `Missing commune for child '${child.person.lastname} ${child.person.firstname}' booking!`
    )
  }

  return {
    allocation,
    u3,
    subsidyState,
    subsidyStateWithoutAssistedIntegration: getSubsidyState(booking, child, u3, false),
    bookingTime,
    communeKey: allocation == Allocation.Foerderfaehig ? booking.commune?.communeKey || "" : "",
    // Helpful document for the next two keys:
    // https://digitale-verwaltung.atlassian.net/wiki/download/attachments/1277100040/adebisKITA-Elternbeitragszuschss.pdf
    parentalFeeExemption: "", // only used January-March 2019 -> we ignore it
    // Note: I *think* that KiBiG just wants to know whether we "request" the subsidy.
    //       It will most certainly check the actual eligibility itself. (see linked PDF)
    // Right now we check eligibility in the analysis.ts (see `eligibleForParentalFeeSubsidy`)
    parentalFeeSubsidy:
      allocation != Allocation.Foerderfaehig
        ? ""
        : !booking.exceptionForParentalFeeSubsidy
          ? "1"
          : "0",
    assistedIntegration: !!booking.assistedIntegration,
    districtSubsidy: !!booking.districtSubsidy,
    isSchoolChild: !!booking.isSchoolChild,
  }
}

/**
 * Porsch "Zeitpunkt der Vollendung des dritten Lebensjahres"
 */
export function eligibleForParentalFeeSubsidy(
  completedYearOfLifeAtEoY: number,
  month: Month,
  isSchoolChild: boolean
) {
  return (
    ((completedYearOfLifeAtEoY === 3 && month >= Month.SEPTEMBER) ||
      completedYearOfLifeAtEoY > 3) &&
    !isSchoolChild
  )
}

export type ChildInfoForPatterns = {
  id: string
  firstName: string
  lastName: string
  childId?: number | null
  altChildId?: string | null
}

export function getKibigChildId(childInfo: ChildInfoForPatterns, kibigConfig?: KibigChildConfig) {
  const { id, firstName, lastName, childId, altChildId } = childInfo
  const fallback = childId != null ? String(childId) : ""

  if (!kibigConfig) return fallback || id

  const { altId, childIdMuster } = kibigConfig

  if (childIdMuster) {
    const kibigChildId = formatPattern(
      childIdMuster,
      { firstName, lastName, childId },
      kibigChildIdMusterTokens
    )

    return kibigChildId
  }

  if (altId) return altChildId ?? ""

  return fallback
}

export function getKibigChildName(childInfo: ChildInfoForPatterns, kibigConfig?: KibigChildConfig) {
  const { firstName, lastName, childId } = childInfo
  const fullName = `${lastName} ${firstName}`.trim()

  if (!kibigConfig) return fullName

  const { childNameMuster } = kibigConfig

  if (childNameMuster) {
    const kibigChildId = formatPattern(
      childNameMuster,
      { firstName, lastName, childId },
      kibigChildNameTokens
    )
    return kibigChildId
  }

  return fullName
}

export function areGeneratedChildIdsUnique(children: KibigChild[]): boolean {
  return areFieldValuesUnique(children, "id")
}
